// frontend/src/components/AdminPaymentStatusPage.js
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CreditCard, Search } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { getAdminPaymentStatuses, getCohorts } from '../services/api';
import { stringify } from 'csv-stringify/browser/esm';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import ExcelJS from 'exceljs';
import { notoSansJPFont } from '../fonts/notosansjp-font.js';

const formatJPY = (amount) => `¥${Number(amount || 0).toLocaleString('ja-JP')}`;

const PAYMENT_COLUMNS = [
  { key: 'username', label: 'ユーザー名', getValue: (entry) => entry.username || '' },
  { key: 'email', label: 'メール', getValue: (entry) => entry.email || '' },
  { key: 'cohort', label: 'コホート', getValue: (entry) => entry.cohort_name || '未設定' },
  { key: 'is_paid', label: '支払い状態', getValue: (entry) => (entry.is_paid ? '支払い済み' : '未払い') },
  {
    key: 'paid_source',
    label: '支払い元',
    getValue: (entry) => {
      if (entry.paid_source === 'payment') return '本人';
      if (entry.paid_source === 'proxy') return '代理';
      return '';
    },
  },
  { key: 'proxy_payer', label: '代理支払者', getValue: (entry) => entry.proxy_payer_username || '' },
  { key: 'total_paid_amount', label: '支払合計', getValue: (entry) => formatJPY(entry.total_paid_amount) },
  { key: 'paid_units', label: '支払枠', getValue: (entry) => entry.paid_units ?? 0 },
  { key: 'allocated_units', label: '代理済み', getValue: (entry) => entry.allocated_units ?? 0 },
  { key: 'available_units', label: '代理枠残', getValue: (entry) => entry.available_units ?? 0 },
  { key: 'latest_payment_status', label: '最新ステータス', getValue: (entry) => entry.latest_payment_status || '' },
];

const AdminPaymentStatusPage = () => {
  const [paymentStatuses, setPaymentStatuses] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [selectedCohort, setSelectedCohort] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedColumns, setSelectedColumns] = useState(PAYMENT_COLUMNS.map((column) => column.key));
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError('');
      try {
        const [statusData, cohortData] = await Promise.all([
          getAdminPaymentStatuses(),
          getCohorts().catch(() => []),
        ]);
        setPaymentStatuses(Array.isArray(statusData) ? statusData : []);
        const sortedCohorts = Array.isArray(cohortData)
          ? [...cohortData].sort((a, b) => {
            if (a.name > b.name) return -1;
            if (a.name < b.name) return 1;
            return 0;
          })
          : [];
        setCohorts(sortedCohorts);
      } catch (err) {
        setError('支払い状況の取得に失敗しました。');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredStatuses = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return paymentStatuses.filter((entry) => {
      const matchesSearch =
        !term ||
        entry.username?.toLowerCase().includes(term) ||
        entry.email?.toLowerCase().includes(term);

      const matchesCohort =
        selectedCohort === 'all' ||
        (selectedCohort === 'none' && !entry.cohort_id) ||
        String(entry.cohort_id) === selectedCohort;

      return matchesSearch && matchesCohort;
    });
  }, [paymentStatuses, searchTerm, selectedCohort]);

  const renderPaidBadge = (isPaid) => (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${isPaid
        ? 'bg-emerald-100 text-emerald-800'
        : 'bg-gray-100 text-gray-600'
        }`}
    >
      {isPaid ? '支払い済み' : '未払い'}
    </span>
  );

  const renderSourceBadge = (source) => {
    if (source === 'payment') {
      return <span className="text-xs font-semibold text-blue-600">本人</span>;
    }
    if (source === 'proxy') {
      return <span className="text-xs font-semibold text-amber-600">代理</span>;
    }
    return <span className="text-xs text-gray-400">-</span>;
  };

  const selectedColumnDefs = useMemo(
    () => PAYMENT_COLUMNS.filter((column) => selectedColumns.includes(column.key)),
    [selectedColumns]
  );

  const toggleColumn = (key) => {
    setSelectedColumns((prev) => (
      prev.includes(key)
        ? prev.filter((columnKey) => columnKey !== key)
        : [...prev, key]
    ));
  };

  const selectAllColumns = () => setSelectedColumns(PAYMENT_COLUMNS.map((column) => column.key));
  const clearAllColumns = () => setSelectedColumns([]);

  const buildRows = (rows) => (
    rows.map((entry) => (
      selectedColumnDefs.map((column) => String(column.getValue(entry) ?? ''))
    ))
  );

  const handleExportCsv = () => {
    if (selectedColumnDefs.length === 0) {
      alert('出力するカラムを選択してください。');
      return;
    }
    const header = selectedColumnDefs.map((column) => column.label);
    const data = buildRows(filteredStatuses);
    const BOM = '\uFEFF';
    stringify([header, ...data], { bom: true, quoted_string: true }, (err, csv) => {
      if (err) {
        alert('CSVの生成に失敗しました。');
        return;
      }
      const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'payment_status.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    });
  };

  const handleExportExcel = async () => {
    if (selectedColumnDefs.length === 0) {
      alert('出力するカラムを選択してください。');
      return;
    }
    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'crikura-ii';
    workbook.created = new Date();
    const sheet = workbook.addWorksheet('Payment Status');
    const header = selectedColumnDefs.map((column) => column.label);
    sheet.addRow(header);
    filteredStatuses.forEach((entry) => {
      sheet.addRow(selectedColumnDefs.map((column) => column.getValue(entry)));
    });
    header.forEach((_, index) => {
      sheet.getColumn(index + 1).width = 20;
    });
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'payment_status.xlsx');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportPdf = () => {
    if (selectedColumnDefs.length === 0) {
      alert('出力するカラムを選択してください。');
      return;
    }
    const doc = new jsPDF('l', 'mm', 'a4');
    doc.addFileToVFS('NotoSansJP-Regular.ttf', notoSansJPFont);
    doc.addFont('NotoSansJP-Regular.ttf', 'NotoSansJP', 'normal');
    doc.autoTable({
      head: [selectedColumnDefs.map((column) => column.label)],
      body: buildRows(filteredStatuses),
      styles: {
        font: 'NotoSansJP',
        fontSize: 8,
        cellPadding: 2,
        overflow: 'linebreak',
      },
      headStyles: {
        fillColor: [243, 244, 246],
        textColor: 40,
      },
      margin: { top: 16, left: 12, right: 12 },
    });
    doc.save('payment_status.pdf');
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/admin-dashboard"
              className="p-2 -ml-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-900 transition-colors"
              aria-label="管理ダッシュボードに戻る"
            >
              <ArrowLeft size={24} />
            </Link>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-gray-500">Admin Console</p>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <CreditCard className="w-6 h-6 text-blue-600" />
                支払い状況
              </h1>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm space-y-4">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center">
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="ユーザー名・メールで検索"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                className="w-full rounded-md border border-gray-300 pl-9 pr-4 py-2 text-sm"
              />
            </div>
          </div>

          <div className="border-b border-gray-200">
            <nav className="-mb-px flex flex-wrap gap-6" aria-label="Cohort Tabs">
              <button
                type="button"
                onClick={() => setSelectedCohort('all')}
                className={`whitespace-nowrap py-3 px-1 border-b-2 text-sm font-medium ${selectedCohort === 'all'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                すべて
              </button>
              {cohorts.map((cohort) => (
                <button
                  key={cohort.id}
                  type="button"
                  onClick={() => setSelectedCohort(String(cohort.id))}
                  className={`whitespace-nowrap py-3 px-1 border-b-2 text-sm font-medium ${selectedCohort === String(cohort.id)
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                  {cohort.name}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setSelectedCohort('none')}
                className={`whitespace-nowrap py-3 px-1 border-b-2 text-sm font-medium ${selectedCohort === 'none'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                未設定
              </button>
            </nav>
          </div>

          <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-4 space-y-3">
            <div className="flex flex-wrap gap-3 items-center justify-between">
              <span className="text-sm font-semibold text-gray-700">出力カラム</span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={selectAllColumns}
                  className="text-xs font-medium text-blue-600 hover:text-blue-700"
                >
                  全選択
                </button>
                <button
                  type="button"
                  onClick={clearAllColumns}
                  className="text-xs font-medium text-gray-500 hover:text-gray-700"
                >
                  解除
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {PAYMENT_COLUMNS.map((column) => (
                <label key={column.key} className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={selectedColumns.includes(column.key)}
                    onChange={() => toggleColumn(column.key)}
                  />
                  {column.label}
                </label>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              <button
                type="button"
                onClick={handleExportCsv}
                className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50"
              >
                CSV出力
              </button>
              <button
                type="button"
                onClick={handleExportExcel}
                className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50"
              >
                Excel出力
              </button>
              <button
                type="button"
                onClick={handleExportPdf}
                className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50"
              >
                PDF出力
              </button>
            </div>
          </div>

          <div className="text-sm text-gray-500">
            {filteredStatuses.length} 件表示中
          </div>
        </section>

        <section className="rounded-lg border border-gray-200 bg-white shadow-sm">
          {isLoading ? (
            <div className="py-12 text-center text-gray-500">読み込み中...</div>
          ) : error ? (
            <div className="py-12 text-center text-red-600">{error}</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader className="bg-gray-50">
                  <TableRow>
                    <TableHead className="text-gray-700 font-bold whitespace-nowrap">ユーザー名</TableHead>
                    <TableHead className="text-gray-700 font-bold whitespace-nowrap">メール</TableHead>
                    <TableHead className="text-gray-700 font-bold whitespace-nowrap">コホート</TableHead>
                    <TableHead className="text-gray-700 font-bold whitespace-nowrap">支払い状態</TableHead>
                    <TableHead className="text-gray-700 font-bold whitespace-nowrap">支払い元</TableHead>
                    <TableHead className="text-gray-700 font-bold whitespace-nowrap">代理支払者</TableHead>
                    <TableHead className="text-gray-700 font-bold whitespace-nowrap">支払合計</TableHead>
                    <TableHead className="text-gray-700 font-bold whitespace-nowrap">代理枠残</TableHead>
                    <TableHead className="text-gray-700 font-bold whitespace-nowrap">最新ステータス</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredStatuses.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-10 text-gray-500">
                        該当するユーザーはいません
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredStatuses.map((entry) => (
                      <TableRow key={entry.user_id} className="hover:bg-gray-50">
                        <TableCell className="font-medium text-gray-900">{entry.username}</TableCell>
                        <TableCell className="font-mono text-xs text-gray-600">{entry.email}</TableCell>
                        <TableCell className="text-sm text-gray-700">
                          {entry.cohort_name || '未設定'}
                        </TableCell>
                        <TableCell>{renderPaidBadge(entry.is_paid)}</TableCell>
                        <TableCell>{renderSourceBadge(entry.paid_source)}</TableCell>
                        <TableCell className="text-sm text-gray-700">
                          {entry.proxy_payer_username || '-'}
                        </TableCell>
                        <TableCell className="text-sm text-gray-700">{formatJPY(entry.total_paid_amount)}</TableCell>
                        <TableCell className="text-sm text-gray-700">
                          {entry.available_units}
                        </TableCell>
                        <TableCell className="text-xs text-gray-500">
                          {entry.latest_payment_status || '-'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default AdminPaymentStatusPage;
